import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Megaphone, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import FileField from '../../components/FileField'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import { FormCols, FormSection, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import IconButton from '../../components/IconButton'
import CkEditorField from '../../components/CkEditorField'
import { EmptyState } from '../../components/States'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import {
  addAwarenessAttachment,
  getAwareness,
  getAwarenessOptions,
  removeAttachment,
  saveAwareness,
} from './engagementService'
import { awarenessStrings as S } from './strings'
import './AwarenessFormPage.css'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  title: '',
  content: '',
  titleEn: '',
  contentEn: '',
  cover: null,
  isActive: true,
  groupIds: [],
  attachments: [],
}

const EMPTY_ATTACHMENT = { nameAr: '', nameEn: '', fileAr: null, fileEn: null }

/**
 * نشرة توعوية — Views/Awareness/Index.cshtml `#edit_form`.
 *
 * The bulletin is the biggest single form in the panel: four content fields
 * (two of them rich text), a cover, an audience, plus the attachments the
 * legacy hangs off a satellite modal. A sheet could not hold that, which is why
 * the drawer version showed ONE language at a time behind a segmented control
 * and left the attachments out entirely.
 *
 * On a page both languages sit side by side, which is how this content is
 * actually written — you translate against the original rather than from
 * memory. The two satellite modals become the middle step, so the whole
 * bulletin is one flow instead of a form plus two places to remember.
 *
 *   1  content     both titles, both bodies
 *   2  media       cover, attachments
 *   3  audience    live or not, and which departments see it
 */
export default function AwarenessFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack('/awareness')
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [groups, setGroups] = useState([])
  const [draft, setDraft] = useState(EMPTY_ATTACHMENT)
  const [saving, setSaving] = useState(false)
  /* Saved attachments the user dropped, kept until the form is saved. */
  const [removedAttachments, setRemovedAttachments] = useState([])

  useEffect(() => {
    let alive = true
    getAwarenessOptions().then((options) => alive && setGroups(options.groups ?? []))
    if (id) getAwareness(id).then((row) => alive && row && setValues({ ...EMPTY, ...row }))
    return () => {
      alive = false
    }
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }
  const setDraftField = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }))

  const steps = useMemo(
    () => [
      { id: 'content', label: S.form.steps.content },
      { id: 'media', label: S.form.steps.media },
      { id: 'audience', label: S.form.steps.audience },
    ],
    [],
  )

  /* The legacy marks both titles and both bodies required. */
  const validateStep = (index) => {
    if (index !== 0) return true
    const next = {}
    if (!values.title.trim()) next.title = S.form.required
    if (!values.titleEn.trim()) next.titleEn = S.form.required
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const addAttachment = () => {
    if (!draft.nameAr.trim() || !draft.nameEn.trim()) return
    setValues((previous) => ({
      // No `id`: an attachment only gets one from the server, and the absence of one is
      // what marks it as still to be posted.
      ...previous,
      attachments: [...previous.attachments, { ...draft }],
    }))
    setDraft(EMPTY_ATTACHMENT)
  }

  const submit = async () => {
    setSaving(true)
    const outcome = await saveAwareness(values)

    if (!outcome.ok) {
      setSaving(false)
      return toast.error(outcome.message || S.actionFailed)
    }

    /* ATTACHMENTS ARE THEIR OWN CALLS. `SaveAwareness` writes the bulletin and nothing
       else, so this section used to be local state that went nowhere: adding one saved
       nothing and removing one removed nothing. They are posted here, after the save,
       because a NEW bulletin has no id until it returns one. */
    const awarenessId = outcome.id ?? id
    const problems = []
    const posted = []

    for (const attachment of values.attachments.filter((row) => !row.id)) {
      const result = await addAwarenessAttachment({ awarenessId, ...attachment })
      if (result.ok) posted.push(attachment)
      else problems.push(result.message)
    }

    const removedOk = []
    for (const attachmentId of removedAttachments) {
      const result = await removeAttachment(attachmentId)
      if (result.ok) removedOk.push(attachmentId)
      else problems.push(result.message)
    }

    setSaving(false)

    if (problems.length > 0) {
      /* WHAT SUCCEEDED IS DROPPED FROM THE PENDING WORK, so pressing save again retries
         only what failed instead of posting the same attachment twice. */
      setValues((previous) => ({
        ...previous,
        attachments: previous.attachments.filter((row) => !posted.includes(row)),
      }))
      setRemovedAttachments((previous) => previous.filter((key) => !removedOk.includes(key)))

      // The bulletin IS saved; only some of its attachments are not, and saying so is the
      // difference between a retry and a re-entry of the whole form.
      return toast.error(
        problems[0] ? `${S.form.attachmentsFailed} — ${problems[0]}` : S.form.attachmentsFailed,
      )
    }

    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/awareness')
  }

  const removeAt = (key, index) => {
    // A SAVED attachment is remembered rather than forgotten: dropping it from the list is
    // not a deletion until the form is saved.
    if (key === 'attachments') {
      const removed = values.attachments[index]
      if (removed?.id) setRemovedAttachments((previous) => [...previous, removed.id])
    }
    setValues((previous) => ({
      ...previous,
      [key]: previous[key].filter((_, i) => i !== index),
    }))
  }

  return (
    <FormPage
      icon={Megaphone}
      title={id ? S.row.edit : S.create}
      subtitle={S.title}
      steps={steps}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {({ step }) => {
        if (step === 0) {
          return (
            <>
              {/* Side by side, not one behind a language switch: this content is
                  written as a translation, and you write a translation against
                  the original rather than from memory. */}
              <FormSection>
                <FormCols>
                  <FormRow label={S.form.titleAr} error={errors.title}>
                    {(fid) => (
                      <TextField
                        id={fid}
                        value={values.title}
                        onChange={set('title')}
                        invalid={!!errors.title}
                        autoComplete="off"
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.titleEn} error={errors.titleEn}>
                    {(fid) => (
                      <TextField
                        id={fid}
                        dir="ltr"
                        value={values.titleEn}
                        onChange={set('titleEn')}
                        invalid={!!errors.titleEn}
                        autoComplete="off"
                      />
                    )}
                  </FormRow>
                </FormCols>
              </FormSection>

              {/* CKEditor, not the seven-button `RichTextField`.
                  A bulletin is the one thing in this product authored as a DOCUMENT —
                  headings, tables, embedded media, uploaded images — and the legacy gives
                  this exact screen the CKEditor super build
                  (`Views/Awareness/Index.cshtml:596`) while giving other HTML fields a bare
                  textarea. Matching that is what the reference asks for. */}
              <div className="awzwiz__bodies">
                <FormRow label={S.form.contentAr}>
                  {(fid) => (
                    <CkEditorField
                      id={fid}
                      dir="rtl"
                      value={values.content}
                      onChange={set('content')}
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.contentEn}>
                  {(fid) => (
                    <CkEditorField
                      id={fid}
                      dir="ltr"
                      value={values.contentEn}
                      onChange={set('contentEn')}
                    />
                  )}
                </FormRow>
              </div>
            </>
          )
        }

        if (step === 1) {
          return (
            <>
              <FormSection>
                <FormCols>
                  <FormRow label={S.form.cover}>
                    {(fid) => (
                      <FileField id={fid} accept="image/*" onChange={set('cover')} />
                    )}
                  </FormRow>
                  {/* THE VIDEO PICKER IS GONE — see `COSMETIC_UI.md`, "the videos list".
                      It collected a file, listed its name, and sent nothing: `saveAwareness`
                      never read the state it wrote and `/upload-video` was never called.
                      Wiring it would not have helped either — `Awareness` has no video
                      column and the learner's bulletin has no player — so the step now
                      offers only the cover, which does reach the server. */}
                </FormCols>
              </FormSection>

              <FormSection>
                {/* The legacy's attachments modal, inline: a name and a file per
                    language, added one at a time to a list. */}
                <div className="awzwiz__adder">
                  <FormCols>
                    <FormRow label={S.form.attachmentNameAr}>
                      {(fid) => (
                        <TextField
                          id={fid}
                          value={draft.nameAr}
                          onChange={setDraftField('nameAr')}
                          autoComplete="off"
                        />
                      )}
                    </FormRow>
                    <FormRow label={S.form.attachmentNameEn}>
                      {(fid) => (
                        <TextField
                          id={fid}
                          dir="ltr"
                          value={draft.nameEn}
                          onChange={setDraftField('nameEn')}
                          autoComplete="off"
                        />
                      )}
                    </FormRow>
                    <FormRow label={S.form.fileAr}>
                      {(fid) => (
                        <FileField
                          id={fid}
                          accept=".png,.jpg,.jpeg,.gif,.doc,.docx,.ppt,.pptx,.pdf"
                          onChange={setDraftField('fileAr')}
                        />
                      )}
                    </FormRow>
                    <FormRow label={S.form.fileEn}>
                      {(fid) => (
                        <FileField
                          id={fid}
                          accept=".png,.jpg,.jpeg,.gif,.doc,.docx,.ppt,.pptx,.pdf"
                          onChange={setDraftField('fileEn')}
                        />
                      )}
                    </FormRow>
                  </FormCols>

                  <div className="awzwiz__adder-moves">
                    <Button
                      type="button"
                      onClick={addAttachment}
                      disabled={!draft.nameAr.trim() || !draft.nameEn.trim()}
                    >
                      <Plus size={16} strokeWidth={2} />
                      {S.form.addAttachment}
                    </Button>
                  </div>
                </div>

                {values.attachments.length === 0 ? (
                  <EmptyState title={S.form.noAttachments} />
                ) : (
                  <ul className="awzwiz__list">
                    {values.attachments.map((attachment, index) => (
                      <li key={attachment.id ?? index} className="awzwiz__row">
                        <span className="awzwiz__row-titles">
                          <bdi className="awzwiz__row-name">{attachment.nameAr}</bdi>
                          <bdi className="awzwiz__row-sub" dir="ltr">
                            {attachment.nameEn}
                          </bdi>
                        </span>
                        <IconButton
                          icon={Trash2}
                          label={S.form.remove}
                          tone="danger"
                          onClick={() => removeAt('attachments', index)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </FormSection>
            </>
          )
        }

        return (
          <FormSection>
            <SwitchField
              checked={values.isActive}
              onChange={set('isActive')}
              label={S.form.isActive}
            />

            <GroupPicker
              options={groups}
              selected={values.groupIds}
              onChange={set('groupIds')}
              legend={S.form.groups}
              error={errors.groupIds}
              selectAllLabel={S.form.selectAll}
              filterLabel={S.form.filter}
            />
          </FormSection>
        )
      }}
    </FormPage>
  )
}
